import { FormEvent, useCallback, useMemo, useState } from 'react';
import { getUsersRequest } from 'actions/userActions';
import { User, AugmentedBuilding, UserId } from 'shared/models';
import { useTranslation } from 'react-i18next';
import { ErrorMessage } from 'ui/shared/messages';
import { CustomSelectField } from 'ui/shared/controls';
import { useDispatch, useSelector } from 'react-redux';
import { useSession } from 'hooks';
import { Permission } from 'shared/permissions';
import { getBuildingOrgs } from 'selectors/buildingOrgsSelectors';
import { State } from 'reducers';
import { merge } from 'lodash';
import UUID from 'util/UUID';

require('./AssignUserForm.css');

export type Props = {
    building: AugmentedBuilding | null;
    isSubmitting: boolean;
    errorMessage: string;
    onAssign(userId: UserId): void;
    onCancel(): void;
};


export const AssignUserForm = ({ building, isSubmitting, errorMessage, onAssign, onCancel }: Props) => {
    const { t } = useTranslation();
    const { hasPermission, user } = useSession();

    const buildingUserIds = building?.userIds || [];
    const buildingId = building?.id;
    const buildingOrgs = useSelector((state: State) => getBuildingOrgs(state, { buildingId }));

    const [ selectedUserId, setSelectedUserId ] = useState<UserId>();

    const dispatch = useDispatch();
    
    const handleSubmit = useCallback((e: FormEvent) => {
        e.preventDefault();
        onAssign(selectedUserId!);
    }, [ selectedUserId, onAssign ]);

    const handleSearch = useCallback((searchText: string, searchResultsCallback: (options: Record<string, unknown>) => void) => {
        // Only show the user's own organization unless they have AllOrgs permission
        const orgs =
            hasPermission(null, Permission.AllOrganizations)
                ? merge(
                    { 'null': true },
                    ...buildingOrgs.map(bo => ({ [bo.orgId]: true })),
                )
                : { [user?.orgId || 'null']: true };

        // The saga that requests user data will call this callback once the users have arrived from the server.
        // And then we delegate to the callback for the Semantic UI select field
        const callback = (users: User[], more: boolean) => {
            searchResultsCallback({
                success: true,
                results:
                    users
                        // Commented out because the users that come back from the API only have roleId info and no permissionIds.
                        //.filter(({ permissions }) => !hasPermission(null, Permission.AllBuildings) && !hasPermission(null, Permission.AllOrganizations)) // Exclude admin users
                        .filter(({ id }) => !buildingUserIds.includes(id)) // Exclude users that are already in the building
                        .map(u => ({
                            name: u.displayName || u.login,
                            value: u.id,
                        })),
            });
        };

        dispatch(getUsersRequest({
            searchText,
            orgs,
        }, {
            callback,
        }));
    }, [ dispatch, buildingUserIds ]);

    const handleChange = useCallback((value: string) => {
        setSelectedUserId(value as UserId);
    }, [ setSelectedUserId ]);

    // Documentation of apiSettings is here https://semantic-ui.com/behaviors/api.html#/settings
    const apiSettings = useMemo(() => ({
        responseAsync: (settings: any, searchResultsCallback: (options: Record<string, unknown>) => void) => {
            const { urlData: { query: searchText } } = settings;
            handleSearch(searchText, searchResultsCallback);
        },
    }), [ handleSearch ]);

    return (
        <form
            id="assign-user-form"
            onSubmit={ handleSubmit }
            className={ `ui form${errorMessage ? ' error' : ''}${isSubmitting ? ' loading' : ''}` }
        >
            <div className="ui grid">
                <div className="sixteen wide column">
                    <CustomSelectField
                        id="assign-user-id"
                        labelKey="USERS.ASSIGN_DIALOG.FIELD.SEARCH"
                        promptKey="USERS.ASSIGN_DIALOG.PROMPT"
                        required={ true }
                        value={ selectedUserId || '' }
                        onChange={ handleChange }
                        apiSettings={ apiSettings }
                    />
                </div>
            </div>

            <div className="__buttons">
                <button
                    className="ui primary button"
                    type="submit"
                    disabled={ isSubmitting || !selectedUserId }
                >
                    { t('COMMAND.ASSIGN') }
                </button>
                <button
                    className="ui button"
                    type="button"
                    disabled={ isSubmitting }
                    onClick={ onCancel }
                >
                    { t('COMMAND.CANCEL') }
                </button>
            </div>
            <ErrorMessage message={ errorMessage }/>
        </form>
    );
};
